import { forwardRef, type InputHTMLAttributes, type MouseEvent, type TextareaHTMLAttributes } from "react";

import { cn } from "@/lib/utils";

const inputClassName =
  "h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-100";

function openNativeDatePicker(input: HTMLInputElement): void {
  if (input.disabled || input.readOnly) return;
  if (typeof input.showPicker !== "function") return;
  try {
    input.showPicker();
  } catch {
    // Some browsers throw when the picker is already open or unsupported.
  }
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...rest }, ref) => (
    <input ref={ref} className={cn(inputClassName, className)} {...rest} />
  ),
);
Input.displayName = "Input";

/** Native date field that opens the browser calendar on click (not only the tiny icon). */
export const DateInput = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, "type">>(
  ({ className, disabled, readOnly, onClick, ...rest }, ref) => {
    const handleClick = (event: MouseEvent<HTMLInputElement>): void => {
      onClick?.(event);
      if (event.defaultPrevented) return;
      openNativeDatePicker(event.currentTarget);
    };

    return (
      <input
        ref={ref}
        type="date"
        disabled={disabled}
        readOnly={readOnly}
        className={cn(
          inputClassName,
          "[&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-100",
          className,
        )}
        onClick={handleClick}
        {...rest}
      />
    );
  },
);
DateInput.displayName = "DateInput";

export const TextArea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className, ...rest }, ref) => (
    <textarea
      ref={ref}
      className={cn(
        "min-h-[6rem] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-mono text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500",
        className,
      )}
      {...rest}
    />
  ),
);
TextArea.displayName = "TextArea";

export function Label({
  htmlFor,
  children,
  className,
}: {
  htmlFor?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label htmlFor={htmlFor} className={cn("block text-xs font-medium text-slate-700", className)}>
      {children}
    </label>
  );
}
